import React, { createContext, useState, useEffect, useContext } from "react";
import api from "../api/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load user data from local storage and then sync with cookie-backed session.
  useEffect(() => {
    const bootstrapAuth = async () => {
      const storedAuthData = localStorage.getItem("authData");
      if (storedAuthData) {
        try {
          const { user: storedUser } = JSON.parse(storedAuthData);

          if (storedUser) {
            setUser(storedUser);
            setIsLoggedIn(true);
          }
        } catch (e) {
          console.error("Failed to parse auth data from localStorage", e);
          localStorage.removeItem("authData");
        }
      }

      try {
        // Sync auth state from HttpOnly cookie session when present.
        const { data } = await api.get("/auth/me");
        if (data?.user) {
          setUser(data.user);
          setIsLoggedIn(true);
          localStorage.setItem("authData", JSON.stringify({ user: data.user }));
        }
      } catch (error) {
        setUser(null);
        setIsLoggedIn(false);
        localStorage.removeItem("authData");
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const login = (userData) => {
    const authData = { user: userData };
    localStorage.setItem("authData", JSON.stringify(authData));
    setUser(userData);
    setIsLoggedIn(true);
  };

  // ✅ New: Function to update the user data in the state and local storage
  const updateUser = (updatedUserData) => {
    // Update state
    setUser(updatedUserData);

    // Update local storage
    const authData = { user: updatedUserData };
    localStorage.setItem("authData", JSON.stringify(authData));
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Continue local logout even if network logout fails.
    }
    setUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem("authData");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn,
        login,
        logout,
        loading,
        updateUser, // ✅ Make the new function available to other components
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
